import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import colors from 'theme/colors';
import { panelAccent } from 'theme/panelAccent';

// Só até o header ser medido; a altura real vem do próprio elemento.
const FALLBACK_HEADER_BOTTOM = 96;

/** O header do painel é fixo: a barra gruda logo abaixo dele, onde quer que ele termine. */
function useHeaderBottom() {
    const [bottom, setBottom] = useState(FALLBACK_HEADER_BOTTOM);

    useEffect(() => {
        const header = Array.from(document.querySelectorAll('header'))
            .find(element => getComputedStyle(element).position === 'fixed');

        if (!header) {
            return undefined;
        }

        const measure = () => setBottom(Math.floor(header.getBoundingClientRect().bottom));
        measure();

        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', measure);
            return () => window.removeEventListener('resize', measure);
        }

        const observer = new ResizeObserver(measure);
        observer.observe(header);

        return () => observer.disconnect();
    }, []);

    return bottom;
}

/**
 * As abas da análise (Resumo, Perfil, Mercado...). Sobem com a página até
 * encostar no header e ficam fixas dali em diante, para a pessoa trocar de aba
 * em qualquer ponto da rolagem. Grudada, a barra vira uma faixa de ponta a ponta.
 */
export default function AnalysisTabBar({ tabs, value, onChange }) {
    const top = useHeaderBottom();
    const sentinelRef = useRef(null);
    const scrollerRef = useRef(null);
    const tabRefs = useRef({});
    const [stuck, setStuck] = useState(false);

    // Um ponto de 1px na posição natural da barra: quando ele sai por cima do
    // header, a barra está grudada.
    useEffect(() => {
        const sentinel = sentinelRef.current;

        if (!sentinel || typeof IntersectionObserver === 'undefined') {
            return undefined;
        }

        const observer = new IntersectionObserver(
            ([entry]) => setStuck(!entry.isIntersecting && entry.boundingClientRect.top < top),
            { rootMargin: `-${top}px 0px 0px 0px`, threshold: 0 }
        );
        observer.observe(sentinel);

        return () => observer.disconnect();
    }, [top]);

    // No celular a lista rola de lado: a aba escolhida precisa ficar à vista.
    useEffect(() => {
        const scroller = scrollerRef.current;
        const tab = tabRefs.current[value];

        if (scroller && tab) {
            scroller.scrollTo({ left: tab.offsetLeft - (scroller.clientWidth - tab.clientWidth) / 2, behavior: 'smooth' });
        }
    }, [value]);

    function select(name) {
        // Rolando lá embaixo, o conteúdo da outra aba começa logo abaixo da barra.
        if (stuck && sentinelRef.current) {
            window.scrollTo({ top: window.scrollY + sentinelRef.current.getBoundingClientRect().top - top });
        }

        onChange(name);
    }

    function handleKeyDown(event, index) {
        const last = tabs.length - 1;
        const target = {
            ArrowRight: index === last ? 0 : index + 1,
            ArrowLeft: index === 0 ? last : index - 1,
            Home: 0,
            End: last,
        }[event.key];

        if (target === undefined) {
            return;
        }

        event.preventDefault();
        select(tabs[target].name);
        tabRefs.current[tabs[target].name]?.focus();
    }

    return (
        <>
            <Sentinel ref={sentinelRef} aria-hidden='true' />
            <Bar $top={top} $stuck={stuck}>
                <Scroller ref={scrollerRef} role='tablist' aria-label='Seções da análise'>
                    {tabs.map((tab, index) => {
                        const Icon = tab.icon;
                        const active = tab.name === value;

                        return (
                            <Tab
                                key={tab.name}
                                ref={element => { tabRefs.current[tab.name] = element; }}
                                type='button'
                                role='tab'
                                aria-selected={active}
                                tabIndex={active ? 0 : -1}
                                $active={active}
                                onClick={() => select(tab.name)}
                                onKeyDown={event => handleKeyDown(event, index)}
                            >
                                {Icon && <Icon size={15} aria-hidden='true' />}
                                {tab.label}
                            </Tab>
                        );
                    })}
                </Scroller>
            </Bar>
        </>
    );
}

const Sentinel = styled.div`
    height: 1px;
    margin-bottom: -1px;
    pointer-events: none;
`;

const Bar = styled.div`
    position: sticky;
    top: ${({ $top }) => $top}px;
    z-index: 20;
    margin-bottom: 22px;
    background: ${colors.neutralLight};
    border: 1px solid ${colors.neutralDark};
    border-radius: 12px;
    transition: border-radius 0.15s ease, border-color 0.15s ease;

    ${({ $stuck }) => $stuck && css`
        border-radius: 0;
        border-color: transparent;
        /* A faixa vai até as bordas da tela sem aumentar a largura da página
           (sombra não conta para a rolagem), e o recorte deixa só um respiro de
           sombra embaixo. */
        box-shadow: 0 0 0 100vmax ${colors.neutralLight};
        clip-path: inset(0 -100vmax -14px 0);
        filter: drop-shadow(0 3px 4px rgba(26, 26, 26, 0.08));
    `}
`;

const Scroller = styled.div`
    display: flex;
    gap: 2px;
    padding: 0 6px;
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }
`;

const Tab = styled.button`
    position: relative;
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    height: 48px;
    padding: 0 16px;
    border: none;
    background: none;
    color: ${({ $active }) => ($active ? panelAccent(colors.secondaryPurple) : colors.night)};
    font-family: inherit;
    font-size: 14px;
    font-weight: ${({ $active }) => ($active ? 600 : 400)};
    white-space: nowrap;
    cursor: pointer;
    transition: color 0.15s ease;

    &::after {
        content: '';
        position: absolute;
        left: 12px;
        right: 12px;
        bottom: 0;
        height: 3px;
        border-radius: 3px 3px 0 0;
        background: ${panelAccent(colors.secondaryPurple)};
        transform: scaleX(${({ $active }) => ($active ? 1 : 0)});
        transition: transform 0.2s ease;
    }

    &:hover {
        color: ${({ $active }) => ($active ? panelAccent(colors.secondaryPurple) : colors.nightDark)};
    }

    &:focus-visible {
        outline: 2px solid ${panelAccent(colors.secondaryPurple)};
        outline-offset: -4px;
        border-radius: 8px;
    }
`;
